// components/settings/SpaceInfoPanel.jsx — Anamoria SPA
// v1.8 — Consent copy tweak (Oct 4, 2026): quiet-note label may wrap (was forced one line).
// v1.7 — Consent PR E4b (Oct 4, 2026; NEW-99): the quiet-note checkbox (QN-5) inline on each
//   held-close day — birthday and the day they passed (read-only rows) and each custom day (editable
//   row) — plus one "What this means" + QN-6 for the section. Each toggle saves immediately via
//   PATCH /spaces/{id}/note-choices (grant carries the stored-wording hash — B2). The notice is
//   fetched ONCE for the section (useConsentNotice). DARK: gated by key_date_note_capture_enabled AND
//   the notice being stored — renders nothing otherwise (fail-closed).
// v1.6 — E4a fix (Oct 4, 2026): the spaces API returns DATE columns as full ISO timestamps
//   (no pg type parser), which <input type="date"> rejects — so a saved key date came back blank.
//   Normalise the server's date to its YYYY-MM-DD prefix at ingest (toKeyDateRows + POST/PATCH
//   responses). Lambda runs UTC so the slice is exact. Proper source fix (serialise DATE as
//   YYYY-MM-DD in the spaces lambda) tracked in the backlog.
// v1.5 — Consent PR E4a (Oct 4, 2026; NEW-98): "Days to hold close" management in Settings —
//   list/add/edit/remove custom key dates via POST/PATCH/DELETE /spaces/{id}/key-dates, each saved
//   immediately via its own route (like the Space Type switch), with inline Saved/error (QN-11/QN-12)
//   and a remove confirm (QN-10). Gated by relationship_key_dates_enabled (the live Create-a-Space
//   flag) — so this is VISIBLE for enabled accounts, not dark. Standard-date (birthday / day-passed)
//   editing stays out of scope (deferred, pg round-trip). Quiet-note checkboxes are E4b (dark).
// v1.4 — Create-a-Space edit (NEW-96): flag-gated "Who they are to you" relationship section
//   (select + free-text "other"), saved with name/theme via the bottom Save (PATCH relationship).
//   Behind relationship_key_dates_enabled (appState.flags). Editing the two dates in Settings is a
//   follow-up (pg date round-trip needs care). (Sept 17, 2026)
// v1.3 — Phase 2: leader "Space type" switch (group/private) with independent save +
//   pending-review lock (blocks Group→Private while reviews wait). Gated by canSetGroupType. (Sept 3, 2026)
// v1.2 — Space photo management (April 24, 2026)
//
// Changes from v1.1:
//   - Added SPACE PHOTO section between Space Name and Voice Card Style.
//   - If space has a photo (space.photoUrl), shows circular preview with
//     "Change photo" and "Remove photo" buttons.
//   - If no photo, shows dashed circle + camera icon + "Add a photo" label.
//   - Photo upload uses POST /media/upload-url → PUT → included in save payload.
//   - Remove photo sets photoUrl to null in the PATCH body.
//   - Photo changes are saved together with name/theme on "Save" click.
//   - Resolves S3 key to CloudFront signed URL for display via GET /media/playback/{key}.
//
// v1.1 — Screenshot thumbnails replace CSS-drawn previews (April 21, 2026)
// v1.0 — Space name + voice card style picker (April 11, 2026)
//
// Props:
//   space    — current space object (from SettingsPage state)
//   getApi   — stable API client factory
//   onSave   — callback with full updated space object (for parent state sync)

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAppContext } from '../../App';
import { THEME_OPTIONS } from './settingsUtils';
import { RELATIONSHIP_OPTIONS } from '../../constants/relationships'; // E1: shared 14-entry list
import { invalidateCache } from '../../utils/apiCache';
import { useConsentNotice } from '../../hooks/useConsentNotice';
import shared from './settingsShared.module.css';
import styles from './SpaceInfoPanel.module.css';

const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB
const KEY_DATE_MAX = 20; // matches backend KEY_DATE_MAX / CreateSpaceForm
const toDayStr = (d) => (typeof d === 'string' ? d.slice(0, 10) : ''); // DATE may arrive as a full ISO timestamp
const toKeyDateRows = (list) => (list || []).map((k) => { const d = toDayStr(k.date); return { id: k.id, label: k.label || '', date: d, savedLabel: k.label || '', savedDate: d, wantsNote: k.wantsNote === true }; });

export default function SpaceInfoPanel({ space, getApi, onSave, canSetGroupType = false }) {
  // ─── Editable state (initialized from space prop) ───
  const [editName, setEditName] = useState(space.name || '');
  const [editTheme, setEditTheme] = useState(space.voiceCardTheme || 'warm');
  // v1.4: relationship edit (flag-gated)
  const appState = useAppContext();
  const relKeyDatesEnabled = appState?.flags?.relationship_key_dates_enabled === true;
  const [editRelationship, setEditRelationship] = useState(space.relationship || '');
  const [editRelationshipDetail, setEditRelationshipDetail] = useState(space.relationshipDetail || '');
  // v1.3: space-type switch (leaders in group-enabled pilots) — independent save.
  const [isGroup, setIsGroup] = useState(space.isGroupSpace === true);
  const [typeSaving, setTypeSaving] = useState(false);
  const [typeError, setTypeError] = useState(''); // '' | 'save' | 'pending'
  const pendingCount = space.pendingReviewCount ?? 0;

  // ─── E4a: key dates in Settings (NEW-98; gated by relationship_key_dates_enabled) ───
  const [keyDateRows, setKeyDateRows] = useState(() => toKeyDateRows(space.keyDates));
  const [kdDraft, setKdDraft] = useState(null);           // { label, date } | null
  const [kdBusy, setKdBusy] = useState(null);             // id | 'draft' | null
  const [kdError, setKdError] = useState({});             // { [id|'draft']: true }
  const [kdSaved, setKdSaved] = useState({});             // { [id]: true } (QN-11)
  const [kdConfirmRemove, setKdConfirmRemove] = useState(null); // id | null

  // ─── E4b: quiet-note choices (NEW-99; gated by key_date_note_capture_enabled + stored notice) ───
  const noteEnabled = appState?.flags?.key_date_note_capture_enabled === true;
  const noteApi = useMemo(() => getApi(), [getApi]);
  const noteNotice = useConsentNotice(noteApi, 'member_key_date_note', noteEnabled);
  const [stdNote, setStdNote] = useState({ birthday: space.noteChoices?.birthday === true, dayTheyPassed: space.noteChoices?.dayTheyPassed === true });
  const [noteBusy, setNoteBusy] = useState(null);   // 'birthday' | 'day_they_passed' | keyDateId | null
  const [noteError, setNoteError] = useState(null); // same key | null
  const [noteOpen, setNoteOpen] = useState(false);  // "What this means" disclosure

  // ─── Photo state ───
  const [photoS3Key, setPhotoS3Key] = useState(space.photoUrl || null);
  const [photoDisplayUrl, setPhotoDisplayUrl] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null); // local preview for new uploads
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const fileInputRef = useRef(null);

  // ─── Save state ───
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // ─── Timeout ref for cleanup ───
  const savedTimerRef = useRef(null);
  const kdSavedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      if (kdSavedTimerRef.current) clearTimeout(kdSavedTimerRef.current);
    };
  }, []);

  // ─── Re-initialize from space prop when it changes ───
  useEffect(() => {
    setEditName(space.name || '');
    setEditTheme(space.voiceCardTheme || 'warm');
    setEditRelationship(space.relationship || '');
    setEditRelationshipDetail(space.relationshipDetail || '');
    setPhotoS3Key(space.photoUrl || null);
    setPhotoPreview(null);
    setPendingPhotoFile(null);
    setPhotoRemoved(false);
  }, [space.name, space.voiceCardTheme, space.photoUrl, space.relationship, space.relationshipDetail]);

  // ─── E4a: re-seed key dates when a different space loads ───
  useEffect(() => {
    setKeyDateRows(toKeyDateRows(space.keyDates));
    setKdDraft(null); setKdBusy(null); setKdError({}); setKdSaved({}); setKdConfirmRemove(null);
    setStdNote({ birthday: space.noteChoices?.birthday === true, dayTheyPassed: space.noteChoices?.dayTheyPassed === true }); setNoteBusy(null); setNoteError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space.id]);

  const syncKeyDates = useCallback((rows) => {
    if (onSave) onSave({ keyDates: rows.map((r) => ({ id: r.id, label: r.savedLabel, date: r.savedDate })) });
    invalidateCache(`space:${space.id}`);
  }, [onSave, space.id]);

  const handleAddKeyDate = useCallback(async () => {
    if (!kdDraft || kdBusy) return;
    const label = kdDraft.label.trim(), date = kdDraft.date;
    if (!label || !date) return;
    setKdBusy('draft'); setKdError((e) => ({ ...e, draft: false }));
    try {
      const api = getApi();
      const created = await api.post(`/spaces/${space.id}/key-dates`, { label, date });
      const cd = toDayStr(created.date); const row = { id: created.id, label: created.label, date: cd, savedLabel: created.label, savedDate: cd };
      setKeyDateRows((prev) => { const next = [...prev, row]; syncKeyDates(next); return next; });
      setKdDraft(null);
    } catch (e) {
      setKdError((er) => ({ ...er, draft: true }));
    } finally { setKdBusy(null); }
  }, [kdDraft, kdBusy, getApi, space.id, syncKeyDates]);

  const handleSaveKeyDate = useCallback(async (id) => {
    const row = keyDateRows.find((r) => r.id === id);
    if (!row || kdBusy) return;
    const label = row.label.trim(), date = row.date;
    if (!label || !date) { setKdError((e) => ({ ...e, [id]: true })); return; }
    setKdBusy(id); setKdError((e) => ({ ...e, [id]: false }));
    try {
      const api = getApi();
      const updated = await api.patch(`/spaces/${space.id}/key-dates/${id}`, { label, date });
      setKeyDateRows((prev) => {
        const ud = toDayStr(updated.date); const next = prev.map((r) => (r.id === id ? { ...r, label: updated.label, date: ud, savedLabel: updated.label, savedDate: ud } : r));
        syncKeyDates(next); return next;
      });
      setKdSaved((sv) => ({ ...sv, [id]: true }));
      if (kdSavedTimerRef.current) clearTimeout(kdSavedTimerRef.current);
      kdSavedTimerRef.current = setTimeout(() => setKdSaved((sv) => ({ ...sv, [id]: false })), 2000);
    } catch (e) {
      setKdError((er) => ({ ...er, [id]: true }));
    } finally { setKdBusy(null); }
  }, [keyDateRows, kdBusy, getApi, space.id, syncKeyDates]);

  const handleRemoveKeyDate = useCallback(async (id) => {
    if (kdBusy) return;
    setKdBusy(id); setKdError((e) => ({ ...e, [id]: false }));
    try {
      const api = getApi();
      await api.delete(`/spaces/${space.id}/key-dates/${id}`);
      setKeyDateRows((prev) => { const next = prev.filter((r) => r.id !== id); syncKeyDates(next); return next; });
      setKdConfirmRemove(null);
    } catch (e) {
      setKdError((er) => ({ ...er, [id]: true }));
    } finally { setKdBusy(null); }
  }, [kdBusy, getApi, space.id, syncKeyDates]);

  // ─── E4b: toggle one quiet-note choice (one call per change, D1). Grant carries the shown hash (B2). ───
  const toggleNote = useCallback(async ({ dateKind, keyDateId = null, next }) => {
    const key = keyDateId || dateKind;
    if (noteBusy) return;
    setNoteBusy(key); setNoteError(null);
    try {
      const api = getApi();
      await api.patch(`/spaces/${space.id}/note-choices`, {
        dateKind, keyDateId, wantsNote: next, ...(next ? { shownHash: noteNotice.shownHash } : {}),
      });
      if (dateKind === 'custom') setKeyDateRows((prev) => prev.map((r) => (r.id === keyDateId ? { ...r, wantsNote: next } : r)));
      else if (dateKind === 'birthday') setStdNote((n) => ({ ...n, birthday: next }));
      else setStdNote((n) => ({ ...n, dayTheyPassed: next }));
    } catch (e) {
      setNoteError(key);
    } finally { setNoteBusy(null); }
  }, [noteBusy, getApi, space.id, noteNotice.shownHash]);

  // Inline quiet-note checkbox for one day (QN-5). Only when the flag is on AND the wording is stored.
  const noteCheckbox = (dateKind, keyDateId, checked) => {
    if (!noteEnabled || !noteNotice.ready) return null;
    const key = keyDateId || dateKind;
    return (
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexShrink: 1, minWidth: 0, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={checked === true}
          disabled={noteBusy === key}
          aria-label="Send me a quiet note on this day"
          onChange={(e) => toggleNote({ dateKind, keyDateId, next: e.target.checked })}
          style={{ width: 18, height: 18, flex: '0 0 auto', accentColor: 'var(--color-accent, #5b7f6a)' }}
        />
        <span style={{ fontSize: 13, color: '#6b6459', lineHeight: 1.3 }}>Send me a quiet note on this day</span>
      </label>
    );
  };

  // ─── Resolve existing S3 key to CloudFront signed URL for display ───
  useEffect(() => {
    if (!photoS3Key || photoRemoved) {
      setPhotoDisplayUrl(null);
      return;
    }
    let cancelled = false;
    async function resolve() {
      try {
        const api = getApi();
        const data = await api.get(`/media/playback/${photoS3Key}`);
        if (!cancelled && data?.playbackUrl) {
          setPhotoDisplayUrl(data.playbackUrl);
        }
      } catch (err) {
        console.error('Failed to resolve space photo URL:', err);
        if (!cancelled) setPhotoDisplayUrl(null);
      }
    }
    resolve();
    return () => { cancelled = true; };
  }, [photoS3Key, photoRemoved, getApi]);

  // ─── Photo handlers ───

  const handlePhotoClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handlePhotoChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setPhotoError('Please select an image file.');
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      setPhotoError('Photo must be under 5MB.');
      return;
    }

    setPhotoError('');
    setPendingPhotoFile(file);
    setPhotoRemoved(false);

    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleRemovePhoto = useCallback(() => {
    setPhotoPreview(null);
    setPendingPhotoFile(null);
    setPhotoRemoved(true);
    setPhotoError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ─── Save handler (includes photo upload) ───
  // v1.3: toggle group/private; saves immediately. Group->Private is blocked while reviews pend
  // (client-side via pendingCount, and as a backstop on the backend 409 PENDING_REVIEWS_EXIST).
  const handleToggleType = useCallback(async () => {
    if (typeSaving) return;
    const next = !isGroup;
    if (!next && pendingCount > 0) { setTypeError('pending'); return; }
    setTypeSaving(true); setTypeError(''); setIsGroup(next);
    try {
      const api = getApi();
      const updated = await api.patch(`/spaces/${space.id}`, { isGroupSpace: next });
      if (onSave) onSave(updated);
    } catch (e) {
      setIsGroup(!next);
      if (e && (e.status === 409 || e.error === 'PENDING_REVIEWS_EXIST')) setTypeError('pending');
      else setTypeError('save');
    } finally {
      setTypeSaving(false);
    }
  }, [isGroup, pendingCount, typeSaving, getApi, space.id, onSave]);

  const handleSave = useCallback(async () => {
    const trimmedName = editName.trim();
    if (!trimmedName) {
      setError('Space name cannot be empty.');
      return;
    }

    setSaving(true);
    setError('');
    setSaved(false);

    try {
      const api = getApi();
      const body = {
        name: trimmedName,
        voiceCardTheme: editTheme,
      };
      // v1.4: relationship (flag-gated). Empty clears it; detail only for "other".
      if (relKeyDatesEnabled) {
        body.relationship = editRelationship || null;
        body.relationshipDetail = editRelationship === 'other' ? (editRelationshipDetail.trim() || null) : null;
      }

      // Handle photo changes
      if (pendingPhotoFile) {
        // Upload new photo to S3, then include key in PATCH
        try {
          const mimeType = pendingPhotoFile.type || 'image/jpeg';
          const ext = mimeType.split('/')[1] || 'jpg';
          const fileName = `space-photo.${ext}`;

          const uploadData = await api.post('/media/upload-url', {
            spaceId: space.id,
            albumId: null,
            fileName,
            mimeType,
            mediaType: 'photo',
          });

          if (!uploadData?.uploadUrl || !uploadData?.s3Key) {
            throw new Error('Failed to get upload URL');
          }

          const putResponse = await fetch(uploadData.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': mimeType },
            body: pendingPhotoFile,
          });

          if (!putResponse.ok) {
            throw new Error(`S3 upload failed: ${putResponse.status}`);
          }

          body.photoUrl = uploadData.s3Key;
        } catch (photoErr) {
          console.error('Photo upload failed:', photoErr);
          setError('Photo upload failed. Other changes saved.');
          // Continue with save — name and theme still update
        }
      } else if (photoRemoved) {
        // Remove photo — set to null
        body.photoUrl = null;
      }

      const updated = await api.patch(`/spaces/${space.id}`, body);

      if (onSave) onSave(updated);

      // Invalidate space detail cache so SpacePage fetches fresh data
      // (including new photoUrl) on next mount. See Tier A CF-1.
      invalidateCache(`space:${space.id}`);

      // Reset photo pending state after successful save
      setPendingPhotoFile(null);
      setPhotoPreview(null);
      setPhotoRemoved(false);
      if (updated.photoUrl) {
        setPhotoS3Key(updated.photoUrl);
      } else {
        setPhotoS3Key(null);
        setPhotoDisplayUrl(null);
      }

      setSaved(true);
      savedTimerRef.current = setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('SpaceInfoPanel save error:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editName, editTheme, pendingPhotoFile, photoRemoved, getApi, space.id, onSave, relKeyDatesEnabled, editRelationship, editRelationshipDetail]);

  // ─── Determine what photo to show ───
  const displayPhoto = photoPreview || (photoRemoved ? null : photoDisplayUrl);
  const showPhotoPlaceholder = !displayPhoto;

  // ─── Render ───
  return (
    <div>
      {/* ══════ Space Name ══════ */}
      <div className={shared.section}>
        <h3 className={shared.sectionTitle}>SPACE INFO</h3>

        <div className={shared.formGroup}>
          <label className={shared.formLabel}>Space Name</label>
          <input
            type="text"
            className={shared.formInput}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            maxLength={100}
          />
        </div>
      </div>

      {/* ══════ Who they are to you (v1.4, flag-gated) ══════ */}
      {relKeyDatesEnabled && (
        <div className={shared.section}>
          <h3 className={shared.sectionTitle}>WHO THEY ARE TO YOU</h3>
          <p className={shared.hint}>Optional. You can add or change this anytime.</p>
          <div className={shared.formGroup}>
            <select
              className={shared.formInput}
              value={editRelationship}
              onChange={(e) => setEditRelationship(e.target.value)}
              aria-label="Who they are to you"
            >
              <option value="">Choose one</option>
              {RELATIONSHIP_OPTIONS.map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
          {editRelationship === 'other' && (
            <div className={shared.formGroup}>
              <input
                type="text"
                className={shared.formInput}
                value={editRelationshipDetail}
                onChange={(e) => setEditRelationshipDetail(e.target.value)}
                placeholder="In your own words"
                maxLength={160}
              />
            </div>
          )}
        </div>
      )}

      {/* ══════ Days to hold close (E4a, NEW-98, flag-gated) ══════ */}
      {relKeyDatesEnabled && (
        <div className={shared.section}>
          <h3 className={shared.sectionTitle}>DAYS TO HOLD CLOSE</h3>
          <p className={shared.hint}>Birthdays, anniversaries, and other days.</p>

          {noteEnabled && noteNotice.ready && (
            <div style={{ margin: '-4px 0 14px' }}>
              <p className={shared.hint} style={{ margin: '0 0 2px' }}>Only ever to you, and you can change these anytime.</p>
              <button type="button" onClick={() => setNoteOpen((o) => !o)} aria-expanded={noteOpen} style={{ background: 'none', border: 'none', padding: '2px 0', fontSize: 13, color: 'var(--color-accent, #5b7f6a)', textDecoration: 'underline', cursor: 'pointer' }}>What this means</button>
              {noteOpen && (
                <div style={{ margin: '8px 0 0', padding: '12px 14px', borderRadius: 10, background: 'var(--color-bg, #faf9f7)', border: '1px solid #e0ddd8' }}>
                  {noteNotice.body.split(/\n{2,}/).map((x) => x.trim()).filter(Boolean).map((para, i) => <p key={i} style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.5 }}>{para}</p>)}
                </div>
              )}
              {noteError && (
                <p className={shared.hint} style={{ color: '#b45f4d', margin: '8px 0 0' }}>We weren&rsquo;t able to save that. Nothing has changed. When you&rsquo;re ready, please try again.</p>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {noteEnabled && noteNotice.ready && toDayStr(space.birthdate) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 15 }}>Their birthday</span>
                <span style={{ fontSize: 14, color: '#6b6459', whiteSpace: 'nowrap' }}>{toDayStr(space.birthdate)}</span>
                {noteCheckbox('birthday', null, stdNote.birthday)}
              </div>
            )}
            {noteEnabled && noteNotice.ready && toDayStr(space.dateOfLoss) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 15 }}>The day they passed</span>
                <span style={{ fontSize: 14, color: '#6b6459', whiteSpace: 'nowrap' }}>{toDayStr(space.dateOfLoss)}</span>
                {noteCheckbox('day_they_passed', null, stdNote.dayTheyPassed)}
              </div>
            )}
            {keyDateRows.map((row) => {
              const dirty = row.label !== row.savedLabel || row.date !== row.savedDate;
              const busy = kdBusy === row.id;
              return (
                <div key={row.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="text"
                      className={shared.formInput}
                      style={{ flex: 1 }}
                      value={row.label}
                      maxLength={120}
                      aria-label="Name this day"
                      onChange={(e) => setKeyDateRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, label: e.target.value } : r)))}
                    />
                    <input
                      type="date"
                      className={shared.formInput}
                      style={{ width: 'auto' }}
                      value={row.date}
                      aria-label="Date"
                      onChange={(e) => setKeyDateRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, date: e.target.value } : r)))}
                    />
                    {noteCheckbox('custom', row.id, row.wantsNote)}
                    <button
                      type="button"
                      onClick={() => setKdConfirmRemove(row.id)}
                      aria-label="Remove this day"
                      disabled={busy}
                      style={{ flex: '0 0 auto', border: 'none', background: 'none', cursor: 'pointer', color: '#a59a89', fontSize: 20, lineHeight: 1, padding: '0 4px' }}
                    >&times;</button>
                  </div>
                  {dirty && kdConfirmRemove !== row.id && (
                    <button
                      type="button"
                      className={shared.saveBtn}
                      disabled={busy || !row.label.trim() || !row.date}
                      onClick={() => handleSaveKeyDate(row.id)}
                      aria-label="Save this day"
                      style={{ alignSelf: 'flex-start', padding: '6px 14px', fontSize: 14 }}
                    >
                      {busy ? 'Saving…' : 'Save'}
                    </button>
                  )}
                  {kdSaved[row.id] && <span className={shared.savedFade}>Saved.</span>}
                  {kdError[row.id] && (
                    <p className={shared.hint} style={{ color: '#b45f4d' }}>We weren&rsquo;t able to save that. Nothing has changed. When you&rsquo;re ready, please try again.</p>
                  )}
                  {kdConfirmRemove === row.id && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '11px 13px', background: '#FBF3EC', borderRadius: 10 }}>
                      <span style={{ fontSize: '0.85rem', color: '#7d6a53' }}>Remove this day? Any quiet note for it will stop.</span>
                      <div style={{ display: 'flex', gap: 14 }}>
                        <button type="button" onClick={() => handleRemoveKeyDate(row.id)} disabled={busy} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#b45f4d', fontWeight: 600, fontSize: 14 }}>{busy ? 'Removing…' : 'Remove'}</button>
                        <button type="button" onClick={() => setKdConfirmRemove(null)} disabled={busy} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b6459', fontWeight: 600, fontSize: 14 }}>Keep</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {kdDraft && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="text" className={shared.formInput} style={{ flex: 1 }} value={kdDraft.label} maxLength={120} placeholder="Name this day" aria-label="Name this day" onChange={(e) => setKdDraft((d) => ({ ...d, label: e.target.value }))} />
                <input type="date" className={shared.formInput} style={{ width: 'auto' }} value={kdDraft.date} aria-label="Date" onChange={(e) => setKdDraft((d) => ({ ...d, date: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 14 }}>
                <button type="button" className={shared.saveBtn} disabled={kdBusy === 'draft' || !kdDraft.label.trim() || !kdDraft.date} onClick={handleAddKeyDate} style={{ padding: '6px 14px', fontSize: 14 }}>{kdBusy === 'draft' ? 'Adding…' : 'Add'}</button>
                <button type="button" onClick={() => { setKdDraft(null); setKdError((e) => ({ ...e, draft: false })); }} disabled={kdBusy === 'draft'} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b6459', fontWeight: 600, fontSize: 14 }}>Cancel</button>
              </div>
              {kdError.draft && (
                <p className={shared.hint} style={{ color: '#b45f4d' }}>We weren&rsquo;t able to save that. Nothing has changed. When you&rsquo;re ready, please try again.</p>
              )}
            </div>
          )}

          {!kdDraft && keyDateRows.length < KEY_DATE_MAX && (
            <button type="button" onClick={() => setKdDraft({ label: '', date: '' })} style={{ marginTop: 12, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-sage-dark, #5b7a65)', fontWeight: 600, fontSize: 14.5, display: 'flex', alignItems: 'center', gap: 6, padding: '2px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              Add a day
            </button>
          )}
        </div>
      )}

      {/* ══════ Space Type (leaders in group-enabled pilots) ══════ */}
      {canSetGroupType && (
        <div className={shared.section}>
          <h3 className={shared.sectionTitle}>SPACE TYPE</h3>
          <div
            className={shared.toggleRow}
            role="switch"
            aria-checked={isGroup}
            tabIndex={0}
            onClick={handleToggleType}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggleType(); } }}
          >
            <div className={shared.toggleRowLeft}>
              <span className={shared.toggleLabel}>Group space</span>
              <span className={shared.toggleDesc}>You review what contributors add before it&#39;s shared with the group.</span>
            </div>
            <div className={`${shared.toggleTrack} ${isGroup ? shared.toggleOn : ''}`}>
              <div className={shared.toggleKnob} />
            </div>
          </div>
          {((isGroup && pendingCount > 0) || typeError === 'pending') && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, padding: '11px 13px', background: '#FBF3EC', borderRadius: 10 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A0805C" strokeWidth="1.7" strokeLinecap="round" style={{ marginTop: 1, flexShrink: 0 }}><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></svg>
              <span style={{ fontSize: '0.8rem', lineHeight: 1.45, color: '#7d6a53' }}>
                {pendingCount > 0
                  ? <>You have {pendingCount} {pendingCount === 1 ? 'memory' : 'memories'} waiting in <strong>To review</strong>. Please approve or set them aside before turning off group space.</>
                  : <>You still have memories waiting in <strong>To review</strong>. Please approve or set them aside before turning off group space.</>}
              </span>
            </div>
          )}
          {typeError === 'save' && (
            <p className={shared.hint} style={{ color: '#b45f4d', marginTop: 8 }}>We couldn&#39;t change the space type just now. Please try again.</p>
          )}
        </div>
      )}

      {/* ══════ Space Photo ══════ */}
      <div className={shared.section}>
        <h3 className={shared.sectionTitle}>SPACE PHOTO</h3>
        <p className={shared.hint}>This photo appears as the avatar for this space.</p>

        <div className={styles.photoSection}>
          {showPhotoPlaceholder ? (
            <div className={styles.photoPlaceholder} onClick={handlePhotoClick}>
              <div className={styles.photoDashed}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  stroke="currentColor"
                  width="28"
                  height="28"
                  style={{ opacity: 0.4 }}
                >
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </div>
              <span className={styles.photoAddLabel}>Add a photo</span>
            </div>
          ) : (
            <div className={styles.photoPreviewSection}>
              <img
                src={displayPhoto}
                alt={`${space.name} photo`}
                className={styles.photoPreviewCircle}
              />
              <div className={styles.photoActions}>
                <button className={styles.photoActionBtn} onClick={handlePhotoClick}>
                  Change photo
                </button>
                <button
                  className={`${styles.photoActionBtn} ${styles.photoActionRemove}`}
                  onClick={handleRemovePhoto}
                >
                  Remove photo
                </button>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className={styles.fileInput}
          />

          {photoError && <p className={styles.photoError}>{photoError}</p>}
        </div>
      </div>

      {/* ══════ Voice Card Style ══════ */}
      <div className={shared.section}>
        <h3 className={shared.sectionTitle}>VOICE CARD STYLE</h3>
        <p className={shared.hint}>Choose how your voice notes appear in the feed.</p>
        <div className={styles.themePicker}>
          {THEME_OPTIONS.map((theme) => (
            <div
              key={theme.value}
              className={`${styles.themeOption} ${editTheme === theme.value ? styles.themeSelected : ''}`}
              onClick={() => setEditTheme(theme.value)}
              role="radio"
              aria-checked={editTheme === theme.value}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setEditTheme(theme.value);
                }
              }}
            >
              <img
                className={styles.themePreviewImg}
                src={theme.imagePath}
                alt={`${theme.label} voice card theme`}
              />
              <span className={styles.themePickerName}>{theme.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ══════ Footer: Save + feedback ══════ */}
      <div className={shared.panelFooter}>
        <button
          className={shared.saveBtn}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saved && <span className={shared.savedFade}>Saved ✓</span>}
      </div>

      {error && <p className={shared.errorMsg}>{error}</p>}
    </div>
  );
}
